import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowRight, HardHat, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import BrandLogo from "../components/BrandLogo";

// Architectural construction site — high-rise frame against sky.
// Hosted by Unsplash CDN, sized for hero use.
const HERO_IMG =
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1400&q=85";

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user !== null && user !== false) return <Navigate to="/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Welcome back");
      navigate("/dashboard");
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-[#F3F5F7]">
      {/* Left — construction hero (desktop only) */}
      <aside
        className="hidden lg:flex relative w-1/2 xl:w-[58%] overflow-hidden"
        data-testid="login-hero"
        aria-hidden="true"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="eager"
        />
        {/* Strong tint for legibility — headline + bullets sit on top */}
        <div className="absolute inset-0 bg-[#001E44]/55" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#001E44]/40 via-[#001E44]/30 to-transparent" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_70%,_rgba(0,30,68,0.7),_transparent_55%)]" />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-between w-full p-10 xl:p-14 text-white">
          <Link to="/" className="inline-flex items-center" data-testid="hero-brand-link">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center overflow-hidden">
                <img src="/liveprocure-logo.png" alt="liveprocure.com" className="w-full h-full object-cover" />
              </div>
              <div>
                <div className="font-display font-bold tracking-tight text-white text-lg leading-none">
                  <span className="text-white">live</span>
                  <span className="text-[#7CB9FF]">procure</span>
                  <span className="text-[#7CB9FF]">.com</span>
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-blue-200/80 mt-1.5">
                  Construction Procurement
                </div>
              </div>
            </div>
          </Link>

          <div className="max-w-md space-y-6">
            <span className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-blue-200 font-semibold">
              <span className="w-6 h-px bg-blue-200/60" />
              Built for builders
            </span>
            <h2 className="font-display text-4xl xl:text-5xl font-bold tracking-tight leading-[1.05] text-white">
              Procurement that
              <br />
              keeps the site moving.
            </h2>
            <p className="text-base text-blue-100/90 leading-relaxed">
              Track every contractor, every order, every delivery — from approval to install — in one calm dashboard.
            </p>
            <ul className="space-y-2.5 pt-2">
              {[
                "Status bars from approval to delivery",
                "Critical-path alerts when items slip",
                "Share your procurement schedule with anyone",
              ].map((b) => (
                <li key={b} className="flex items-center gap-2.5 text-sm text-blue-50/90">
                  <CheckCircle size={16} weight="fill" className="text-[#7CB9FF] shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          <div className="text-[10px] uppercase tracking-[0.22em] text-blue-200/70 font-medium">
            © {new Date().getFullYear()} liveprocure.com · Made for transparency
          </div>
        </div>
      </aside>

      {/* Right — form */}
      <main className="flex-1 flex items-center justify-center px-4 py-10 sm:py-16 relative">
        {/* Mobile-only floating nav */}
        <nav className="lg:hidden fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[95%] max-w-3xl bg-white/80 backdrop-blur-md rounded-full px-5 py-2.5 flex justify-between items-center shadow-[0_4px_20px_rgb(0,0,0,0.04)] border border-white/40">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <BrandLogo variant="compact" />
          </Link>
          <Link
            to="/signup"
            className="text-xs font-medium text-[#001E44] hover:text-[#007BFF] transition-colors"
            data-testid="nav-signup-link"
          >
            Request access →
          </Link>
        </nav>

        <div className="w-full max-w-md">
          <div className="bg-white rounded-3xl border border-zinc-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8 sm:p-10">
            <div className="space-y-2 mb-8">
              <div className="hidden lg:flex items-center gap-2 mb-4">
                <HardHat size={14} weight="bold" className="text-[#007BFF]" />
                <span className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 font-semibold">
                  Sign in to liveprocure.com
                </span>
              </div>
              <span className="inline-block label-eyebrow text-[#007BFF] lg:hidden">Sign in</span>
              <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#001E44] leading-tight">
                Welcome back to the site.
              </h1>
              <p className="text-sm text-zinc-500 leading-relaxed pt-1">
                Enter your credentials to access your procurement workspace.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs font-semibold text-zinc-700">Work email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-11 px-4"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-semibold text-zinc-700">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-semibold text-[#007BFF] hover:underline"
                    data-testid="link-forgot-password"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="rounded-xl bg-zinc-50 border-zinc-200 focus:bg-white focus:border-[#007BFF] focus:ring-[#007BFF]/15 h-11 px-4"
                />
              </div>

              <Button
                type="submit"
                disabled={busy}
                className="w-full bg-[#007BFF] hover:bg-[#0062cc] rounded-full h-12 text-sm font-semibold shadow-sm hover:shadow-md transition-all"
                data-testid="login-submit-button"
              >
                {busy ? "Signing in..." : <>Sign in <ArrowRight size={16} weight="bold" className="ml-2" /></>}
              </Button>

              <p className="text-sm text-zinc-500 text-center pt-1">
                Need access?{" "}
                <Link to="/signup" className="text-[#007BFF] font-semibold hover:underline" data-testid="link-signup">
                  Request an account
                </Link>
              </p>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}
